import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLogging } from '../providers/LoggingProvider';
import { useThemedComponent, ThemeContent } from '../providers/ThemeProvider';
import { StyleSheet } from '../providers/ThemeProvider';
import { useIdleTimer } from 'react-idle-timer';
import Popup from '../components/atoms/Popup/Popup';
import { Duration } from 'luxon';
import Parse from '../parse';
import i18n from '../translations';

// For testing
// const INACTIVITY_TIMEOUT = Duration.fromObject({ seconds: 10 }).toMillis(); // 10 seconds
// const INACTIVITY_WARNING_TIME = Duration.fromObject({ seconds: 5 }).toMillis(); // 5 seconds
const INACTIVITY_TIMEOUT = Duration.fromObject({ minutes: 30 }).toMillis(); // 30 minutes
const INACTIVITY_WARNING_TIME = Duration.fromObject({ minutes: 25 }).toMillis(); // 25 minutes

type UseInactivityDetectionProps = {
  user: Parse.User | null;
  logout: () => void;
};

export default function useInactivityDetection({
  user,
  logout,
}: UseInactivityDetectionProps) {
  const logger = useLogging(useInactivityDetection.name);
  const { styles } = useThemedComponent([inactivityStyles]);

  const [showInactivityWarning, setShowInactivityWarning] = useState(false);

  /**
   * When the user is idle, show the inactivity warning
   * And pause the warning timer
   */
  const onIdleWarning = () => {
    if (!user) {
      return;
    }

    logger.warn('User is idle, warning!');
    setShowInactivityWarning(true);
    pauseWarning();
  };

  /**
   * Use idle timer to track warning timer
   */
  const { activate: activateWarning, pause: pauseWarning } = useIdleTimer({
    timeout: INACTIVITY_WARNING_TIME,
    throttle: 500,
    onIdle: onIdleWarning,
  });

  /**
   * When the user is idle, log out
   */
  const onIdle = () => {
    if (!user) {
      return;
    }

    logger.warn('User is idle, logging out');
    logout();
  };

  /**
   * Use idle timer to track if the user is inactive for logout
   */
  const { activate: activateIdle, getRemainingTime } = useIdleTimer({
    timeout: INACTIVITY_TIMEOUT,
    throttle: 500,
    onIdle,
  });

  /**
   * Activate the idle timers
   */
  const activate = useCallback(() => {
    if (!user) return;
    activateWarning();
    activateIdle();
  }, [activateWarning, activateIdle, user]);

  /**
   * When the user is logged in, activate the idle timers
   */
  useEffect(() => {
    if (!user) {
      setShowInactivityWarning(false);
      return;
    }
    activate();
  }, [activate, user]);

  /**
   * Render the inactivity warning popup
   */
  const renderInactivityWarning = useMemo(() => {
    if (!showInactivityWarning) return null;

    return (
      <Popup
        onClose={() => {}}
        open={showInactivityWarning}
        xIcon={false}
        title={i18n.t('auth.inactivity.title')}
        buttons={[
          {
            title: i18n.t('auth.inactivity.button'),
            style: styles.button,
            onClick: () => {
              setShowInactivityWarning(false);
              activate(); // Reset the idle timers
            },
          },
        ]}>
        <p style={styles.message}>
          {i18n.t('auth.inactivity.body', {
            minutes: Math.ceil(getRemainingTime() / 60000),
          })}
        </p>
      </Popup>
    );
  }, [
    activate,
    getRemainingTime,
    showInactivityWarning,
    styles.message,
    styles.button,
  ]);

  return {
    renderInactivityWarning,
    getRemainingTime,
  };
}

const inactivityStyles = (theme: ThemeContent): StyleSheet => ({
  container: {
    backgroundColor: theme.colors.white,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 20,
    gap: 20,
  },
  message: {
    textAlign: 'center',
    paddingTop: 30,
    paddingBottom: 10,
  },
  button: {
    backgroundColor: theme.colors.primary,
    color: theme.colors.white,
    padding: '10px 20px',
    borderRadius: 5,
    cursor: 'pointer',
    border: 'none',
  },
});
